// Pure helpers for the portal FAQs, shared by the CSV (Google Sheet) and JSON
// (Notion export on S3) sources. Kept out of the hook so they can be unit tested.

export type FaqFailureKind = "empty" | "unreachable" | "http-error";

export interface FaqFailure {
  kind: FaqFailureKind;
  summary: string;
  status?: number;
  code?: string;
  name: string;
  message: string;
}

/**
 * Why a FAQ source did not produce FAQs.
 *
 * loadFaqs reports three different problems through one callback, and until
 * FS-CUSTOMER-PORTAL-FE-5K they all reached Sentry as one message, so the
 * fingerprint could not be acted on:
 *
 *   empty        the callback is invoked with no error when a source parses but
 *                yields zero usable entries. The file is reachable and its
 *                contents are wrong, which is a data problem.
 *   unreachable  the request never completed, so there is no response to read.
 *                A flaky mobile connection and a CORS rejection are
 *                indistinguishable here; four mobile sessions hit this in
 *                October 2026 while the bucket and its CORS headers were fine.
 *   http-error   the request completed and the server was unhappy.
 *
 * Callers give each kind its own message so Sentry groups them separately.
 */
export const classifyFaqFailure = (error?: unknown): FaqFailure => {
  // Read the shape rather than importing axios: this module is pure helpers by
  // design, and pulling axios in breaks its Jest suite, because axios v1 ships
  // ESM that CRA's default transform does not handle.
  const shaped = error as { response?: { status?: unknown }; code?: unknown } | undefined;
  const rawStatus = shaped?.response?.status;
  const status = typeof rawStatus === "number" ? rawStatus : undefined;
  const code = typeof shaped?.code === "string" ? shaped.code : undefined;
  const name = error instanceof Error ? error.name : typeof error;
  const message = error instanceof Error ? error.message : String(error);
  const base = { code, name, message };

  if (error === undefined) {
    return { ...base, kind: "empty", summary: "returned no usable entries" };
  }
  if (status === undefined) {
    return { ...base, kind: "unreachable", summary: "could not be reached (no response)" };
  }
  return { ...base, kind: "http-error", summary: `failed with HTTP ${status}`, status };
};

export interface FAQ {
  groupId: string;
  groupName: string;
  question: string;
  answer: string;
  link: boolean;
  isProjectStageVisible: boolean | string;
}

export interface GroupedFAQ {
  id: string;
  title: string;
  faqs: {
    groupId: string;
    groupName: string;
    question: string;
    answer: string;
    link: boolean;
    isProjectStageVisible: boolean;
  }[];
}

export interface FaqSource {
  format: "json" | "csv";
  url: string;
}

// Sources to try, in order. The JSON export (REACT_APP_FAQ_JSON_URL_*) comes
// first when it is configured for the customer's audience; the published Google
// Sheet CSV is always last as the fallback.
export const faqSources = (
  isBatteryOnly: boolean,
  env: Record<string, string | undefined> = process.env
): FaqSource[] => {
  const jsonUrl = isBatteryOnly
    ? env.REACT_APP_FAQ_JSON_URL_BATTERY
    : env.REACT_APP_FAQ_JSON_URL_SOLAR;
  const csvUrl = isBatteryOnly
    ? env.REACT_APP_FAQ_BATTERY_GSHEET_URL
    : env.REACT_APP_FAQ_GSHEET_URL;

  const sources: FaqSource[] = [];
  if (jsonUrl) {
    sources.push({ format: "json", url: jsonUrl });
  }
  sources.push({ format: "csv", url: csvUrl || "" });
  return sources;
};

// Returns the FAQs from the first source that yields any. The JSON export does
// not exist until a row is approved in Notion (S3 answers 403), so a failed or
// empty JSON source falls through to the Google Sheet instead of leaving the
// portal with no FAQs.
export const loadFaqs = async (
  sources: FaqSource[],
  fetchSource: (source: FaqSource) => Promise<FAQ[]>,
  onSourceFailed: (source: FaqSource, error?: unknown) => void = () => {}
): Promise<FAQ[]> => {
  for (const source of sources) {
    try {
      const faqs = await fetchSource(source);
      if (faqs.length > 0) {
        return faqs;
      }
      onSourceFailed(source);
    } catch (error) {
      onSourceFailed(source, error);
    }
  }
  return [];
};

// Entries from the JSON export already have the sheet's shape; drop anything
// incomplete, the same way the CSV parser drops rows with an empty cell.
export const parseJsonFaqs = (data: unknown): FAQ[] => {
  if (!Array.isArray(data)) {
    return [];
  }

  return data
    .filter(
      (row: any) =>
        row && row.groupId && row.groupName && row.question && row.answer
    )
    .map((row: any) => ({
      groupId: String(row.groupId),
      groupName: String(row.groupName),
      question: String(row.question),
      answer: String(row.answer),
      link: false,
      isProjectStageVisible: row.isProjectStageVisible,
    }));
};

export const groupFaqs = (dataArray: FAQ[]): GroupedFAQ[] => {
  const groupedData = dataArray.reduce((acc: any, obj: FAQ) => {
    const groupId = obj.groupId;

    if (!acc[groupId]) {
      acc[groupId] = {
        id: groupId,
        title: obj.groupName,
        faqs: [],
      };
    }

    acc[groupId].faqs.push({
      groupId: obj.groupId,
      groupName: obj.groupName,
      question: obj.question,
      answer: obj.answer,
      link: obj.link,
      // The CSV gives the string "TRUE"; the JSON export gives a boolean.
      isProjectStageVisible:
        obj.isProjectStageVisible === true ||
        obj.isProjectStageVisible === "TRUE",
    });

    return acc;
  }, {});

  return Object.values(groupedData);
};
